import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import PreviewDealsListModal from "@app/component/PreviewDealsListModal";
import { DealsGroupByType } from "@app/scene/home/enum/DealsGroupByType";
import { showEntityView } from "@app/store/entityView/actions";
import RottingDealsModalData from "@app/types/ui/RottingDealsModalData";

import useRottingDealListColumns from "../util/useRottingDealListColumns";

export interface Props {
  data: RottingDealsModalData;
  onHide: () => void;
  showEntityView?: typeof showEntityView;
}

const RottingDealsModal: React.FC<Props & typeof mapDispatchToProps> = ({
  data,
  onHide,
  showEntityView,
}) => {
  const intl = useIntl();

  const handleDealClick = useCallback(
    (deal: Deal) => showEntityView?.({ entityId: deal.id, entityType: EntityType.DEAL }),
    [showEntityView]
  );

  const handleCompanyClick = useCallback(
    (entityId: Company["id"]) => showEntityView?.({ entityId, entityType: EntityType.COMPANY }),
    [showEntityView]
  );

  const handlePersonClick = useCallback(
    (entityId: Person["id"]) => showEntityView?.({ entityId, entityType: EntityType.PERSON }),
    [showEntityView]
  );

  const columns = useRottingDealListColumns(handleCompanyClick, handlePersonClick);

  const filter = useMemo(() => {
    let result: FilterModel = {
      rottingDaysOut: { operator: FilterOperator.GREATER_THAN, value: 0 },
    };
    if (data.type === DealsGroupByType.USER) {
      result = { user: { operator: FilterOperator.EQUALS, value: data.id }, ...result };
    } else if (data.type === DealsGroupByType.FUNNEL) {
      result = {
        funnel: { operator: FilterOperator.EQUALS, value: data.id },
        stage: { operator: FilterOperator.EQUALS, value: data.dealId },
        ...result,
      };
    } else {
      result = { team: { operator: FilterOperator.EQUALS, value: data.id }, ...result };
    }
    return result;
  }, [data.id, data.dealId, data.type]);

  return (
    <PreviewDealsListModal
      columns={columns}
      filter={filter}
      onDealClick={handleDealClick}
      onHide={onHide}
      subtitle={data.name}
      title={intl.formatMessage({
        id: "rottingDealListModalForCharts.title",
        defaultMessage: "Rotting Deals",
        description: "Title of rotting deal list modal",
      })}
    />
  );
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(RottingDealsModal);
